/**
 * chartDesign.ts
 *
 * Brand-theme inheritance for chart design controls (#968 Part C — axis
 * label styling, axis titles, gridlines). Per-series color already has its
 * own working hierarchy — widget `color_overrides` merged on top of the
 * dashboard's brand-palette colorMap, see generators/helpers.ts buildColorMap
 * — this module does the same job for everything else that got surfaced:
 *
 *   design-system default  --(explicit override)-->  ChartDesign on the widget
 *
 * There is no per-brand-theme value for axis fonts or gridlines today (a
 * brand theme is a color palette — see BrandTheme in gotypes.ts), so the
 * "theme" side of this hierarchy is a fixed set of design-system defaults
 * (CHART_DESIGN_DEFAULTS) rather than something that varies per dashboard.
 * That still satisfies the override/revert model end to end: every property
 * either has an explicit widget-level value or inherits the one shared
 * default, and reverting always lands on that same default.
 *
 * All functions here are pure — no React, no fetch — so the merge and
 * revert logic is unit-testable without mounting anything. See
 * chartDesign.test.ts.
 */

import type {
  ChartAxisDesign,
  ChartDesign,
  ChartGridlinesDesign,
  ChartGridlineDashStyle,
  ChartLegendDesign,
  ChartLegendPosition,
  ChartDataLabelsDesign,
  ChartDataLabelNumberFormat,
  ChartTitleDesign,
  ChartTitleAlign,
  ChartBarColorsDesign,
  TableConditionalFormatRule,
} from '../types/gotypes'

export interface ResolvedAxisDesign {
  labelFontSize: number
  labelColor: string
  titleText: string
  titleColor: string
  // #1178 — no default value exists for any of these: absent means Auto
  // (the generator/ECharts decides), which is the state every chart starts
  // in and what the group-level Auto button restores.
  min?: number
  max?: number
  majorUnit?: number
  labelInterval?: number
  rangeSlider?: boolean
  /** #1808 — "Balanced around 0"; absent is off. */
  balancedZero?: boolean
}

export interface ResolvedGridlinesDesign {
  color: string
  width: number
  dashStyle: ChartGridlineDashStyle
}

export interface ResolvedLegendDesign {
  show: boolean
  position: ChartLegendPosition
  fontSize: number
  fontColor: string
  showSymbol: boolean
}

export interface ResolvedDataLabelsDesign {
  show: boolean
  numberFormat: ChartDataLabelNumberFormat
}

export interface ResolvedTitleDesign {
  align: ChartTitleAlign
  fontSize: number
  fontWeight: number
  color: string
}

export interface ResolvedChartDesign {
  xAxis: ResolvedAxisDesign
  yAxis: ResolvedAxisDesign
  gridlines: ResolvedGridlinesDesign
  legend: ResolvedLegendDesign
  dataLabels: ResolvedDataLabelsDesign
  title: ResolvedTitleDesign
}

/**
 * The design-system default — matches what the ECharts generators hardcoded
 * before this issue (see generators/line.ts etc: axisLabel fontSize 10/12,
 * a light gray split line). Changing these values changes the default look
 * of every chart that has never been explicitly overridden.
 */
export const CHART_DESIGN_DEFAULTS: ResolvedChartDesign = {
  xAxis: { labelFontSize: 10, labelColor: '#666666', titleText: '', titleColor: '#333333' },
  yAxis: { labelFontSize: 12, labelColor: '#666666', titleText: '', titleColor: '#333333' },
  gridlines: { color: '#E0E0E0', width: 1, dashStyle: 'solid' },
  // Matches chartLegend() in generators/helpers.ts (#1044): top placement,
  // 11px text. Position stays 'top' by default per PR #1068 — bottom
  // collides with the dataZoom slider (see chartLegend's doc comment).
  legend: { show: true, position: 'top', fontSize: 11, fontColor: '#333333', showSymbol: true },
  // Bar/column generators hardcode their own label:{show:true,...} today,
  // independent of this default — this is only what the editor shows for an
  // untouched chart, never what applyChartDesign patches in unasked.
  dataLabels: { show: true, numberFormat: 'auto' },
  // Matches the widget title Typography's current sx (fontWeight 600,
  // color text.primary) in TableGraphWidget.tsx.
  title: { align: 'left', fontSize: 14, fontWeight: 600, color: '#1A1A1A' },
}

function resolveAxis(defaults: ResolvedAxisDesign, override: ChartAxisDesign | undefined): ResolvedAxisDesign {
  if (!override) return defaults
  return {
    labelFontSize: override.label_font_size ?? defaults.labelFontSize,
    labelColor: override.label_color ?? defaults.labelColor,
    titleText: override.title_text ?? defaults.titleText,
    titleColor: override.title_color ?? defaults.titleColor,
    // Optional (#1178): only present when explicitly overridden — Auto is
    // the absence of a value, per the existing revert model.
    ...(override.min != null ? { min: override.min } : {}),
    ...(override.max != null ? { max: override.max } : {}),
    ...(override.major_unit != null ? { majorUnit: override.major_unit } : {}),
    ...(override.label_interval != null ? { labelInterval: override.label_interval } : {}),
    ...(override.range_slider != null ? { rangeSlider: override.range_slider } : {}),
    ...(override.balanced_zero != null ? { balancedZero: override.balanced_zero } : {}),
  }
}

function resolveGridlines(
  defaults: ResolvedGridlinesDesign,
  override: ChartGridlinesDesign | undefined,
): ResolvedGridlinesDesign {
  if (!override) return defaults
  return {
    color: override.color ?? defaults.color,
    width: override.width ?? defaults.width,
    dashStyle: override.dash_style ?? defaults.dashStyle,
  }
}

function resolveLegend(
  defaults: ResolvedLegendDesign,
  override: ChartLegendDesign | undefined,
): ResolvedLegendDesign {
  if (!override) return defaults
  return {
    show: override.show ?? defaults.show,
    position: override.position ?? defaults.position,
    fontSize: override.font_size ?? defaults.fontSize,
    fontColor: override.font_color ?? defaults.fontColor,
    showSymbol: override.show_symbol ?? defaults.showSymbol,
  }
}

/**
 * Chart types whose generator hardcodes `label: { show: true, ... }` in the
 * option it builds (bar.ts, column.ts), independent of ChartDesign. Every
 * other generator (line, scatter, pie, stacked/clustered bar and column)
 * sets no `label` at all, so an untouched chart of those types renders with
 * ECharts' own default — off.
 */
const CHART_TYPES_WITH_NATIVE_DATA_LABELS = new Set(['bar', 'column'])

/**
 * Resolves the effective "Show" default for the editor's Data Labels
 * toggle. This must track the per-chart-type baseline above, not a single
 * fixed default (#1044 follow-up): defaulting the toggle to ON for every
 * chart type made it read "already on" for chart types that don't natively
 * show labels (line, scatter, ...), so nobody ever touched it — and because
 * an untouched control writes no override, applyChartDesign never patched
 * the option, and the chart never got labels. Confirmed live on an
 * 11-series line chart. `chartType` is optional so existing callers that
 * don't have it yet keep the old single-default behavior.
 */
function resolveDataLabels(
  defaults: ResolvedDataLabelsDesign,
  override: ChartDataLabelsDesign | undefined,
  chartType: string | undefined,
): ResolvedDataLabelsDesign {
  const effectiveDefaultShow =
    chartType == null ? defaults.show : CHART_TYPES_WITH_NATIVE_DATA_LABELS.has(chartType)
  if (!override) return { ...defaults, show: effectiveDefaultShow }
  return {
    show: override.show ?? effectiveDefaultShow,
    numberFormat: override.number_format ?? defaults.numberFormat,
  }
}

function resolveTitle(
  defaults: ResolvedTitleDesign,
  override: ChartTitleDesign | undefined,
): ResolvedTitleDesign {
  if (!override) return defaults
  return {
    align: override.align ?? defaults.align,
    fontSize: override.font_size ?? defaults.fontSize,
    fontWeight: override.font_weight ?? defaults.fontWeight,
    color: override.color ?? defaults.color,
  }
}

/**
 * Merges a widget's stored ChartDesign on top of the design-system
 * defaults. `chartType` is optional and only affects the Data Labels
 * "Show" default — see resolveDataLabels.
 */
export function resolveChartDesign(design: ChartDesign | undefined, chartType?: string): ResolvedChartDesign {
  return {
    xAxis: resolveAxis(CHART_DESIGN_DEFAULTS.xAxis, design?.x_axis),
    yAxis: resolveAxis(CHART_DESIGN_DEFAULTS.yAxis, design?.y_axis),
    gridlines: resolveGridlines(CHART_DESIGN_DEFAULTS.gridlines, design?.gridlines),
    legend: resolveLegend(CHART_DESIGN_DEFAULTS.legend, design?.legend),
    dataLabels: resolveDataLabels(CHART_DESIGN_DEFAULTS.dataLabels, design?.data_labels, chartType),
    title: resolveTitle(CHART_DESIGN_DEFAULTS.title, design?.title),
  }
}

/**
 * Every editable design property, addressed the same way in the editor UI,
 * `isChartDesignPropertyOverridden`, `setChartDesignProperty`, and
 * `revertChartDesignProperty` — one vocabulary for "which control is this".
 */
export type ChartDesignPropertyPath =
  | 'xAxis.labelFontSize'
  | 'xAxis.labelColor'
  | 'xAxis.titleText'
  | 'xAxis.titleColor'
  | 'yAxis.labelFontSize'
  | 'yAxis.labelColor'
  | 'yAxis.titleText'
  | 'yAxis.titleColor'
  | 'gridlines.color'
  | 'gridlines.width'
  | 'gridlines.dashStyle'
  | 'legend.show'
  | 'legend.position'
  | 'legend.fontSize'
  | 'legend.fontColor'
  | 'legend.showSymbol'
  | 'dataLabels.show'
  | 'dataLabels.numberFormat'
  | 'title.align'
  | 'title.fontSize'
  | 'title.fontWeight'
  | 'title.color'
  // #1178 — value-axis bounds/step live on the y_axis SECTION but apply to
  // whichever built axis is type 'value' (X on horizontal bar types); the
  // category label interval lives on x_axis and applies to the 'category'
  // axis. applyChartDesign does the routing; these names say what the
  // control means, not which built axis it lands on.
  | 'valueAxis.min'
  | 'valueAxis.max'
  | 'valueAxis.majorUnit'
  | 'categoryAxis.labelInterval'
  | 'categoryAxis.rangeSlider'
  // #1808 — "Balanced around 0", on the y_axis section like min/max.
  | 'valueAxis.balancedZero'

type DesignSection = 'x_axis' | 'y_axis' | 'gridlines' | 'legend' | 'data_labels' | 'title'

const PATH_MAP: Record<ChartDesignPropertyPath, { section: DesignSection; key: string }> = {
  'xAxis.labelFontSize': { section: 'x_axis', key: 'label_font_size' },
  'xAxis.labelColor': { section: 'x_axis', key: 'label_color' },
  'xAxis.titleText': { section: 'x_axis', key: 'title_text' },
  'xAxis.titleColor': { section: 'x_axis', key: 'title_color' },
  'yAxis.labelFontSize': { section: 'y_axis', key: 'label_font_size' },
  'yAxis.labelColor': { section: 'y_axis', key: 'label_color' },
  'yAxis.titleText': { section: 'y_axis', key: 'title_text' },
  'yAxis.titleColor': { section: 'y_axis', key: 'title_color' },
  'gridlines.color': { section: 'gridlines', key: 'color' },
  'gridlines.width': { section: 'gridlines', key: 'width' },
  'gridlines.dashStyle': { section: 'gridlines', key: 'dash_style' },
  'legend.show': { section: 'legend', key: 'show' },
  'legend.position': { section: 'legend', key: 'position' },
  'legend.fontSize': { section: 'legend', key: 'font_size' },
  'legend.fontColor': { section: 'legend', key: 'font_color' },
  'legend.showSymbol': { section: 'legend', key: 'show_symbol' },
  'dataLabels.show': { section: 'data_labels', key: 'show' },
  'dataLabels.numberFormat': { section: 'data_labels', key: 'number_format' },
  'title.align': { section: 'title', key: 'align' },
  'title.fontSize': { section: 'title', key: 'font_size' },
  'title.fontWeight': { section: 'title', key: 'font_weight' },
  'title.color': { section: 'title', key: 'color' },
  'valueAxis.min': { section: 'y_axis', key: 'min' },
  'valueAxis.max': { section: 'y_axis', key: 'max' },
  'valueAxis.majorUnit': { section: 'y_axis', key: 'major_unit' },
  'categoryAxis.labelInterval': { section: 'x_axis', key: 'label_interval' },
  'categoryAxis.rangeSlider': { section: 'x_axis', key: 'range_slider' },
  'valueAxis.balancedZero': { section: 'y_axis', key: 'balanced_zero' },
}

type Section = Record<string, unknown>

/** True when this exact property carries an explicit widget-level override. */
export function isChartDesignPropertyOverridden(
  design: ChartDesign | undefined,
  path: ChartDesignPropertyPath,
): boolean {
  const { section, key } = PATH_MAP[path]
  const sectionValue = design?.[section] as Section | undefined
  return sectionValue !== undefined && sectionValue[key] !== undefined
}

/** Sets one property, creating its section if needed. Pure — returns a new ChartDesign. */
export function setChartDesignProperty(
  design: ChartDesign | undefined,
  path: ChartDesignPropertyPath,
  value: string | number | boolean,
): ChartDesign {
  const { section, key } = PATH_MAP[path]
  const existingSection = (design?.[section] as Section | undefined) ?? {}
  return {
    ...design,
    [section]: { ...existingSection, [key]: value },
  }
}

/**
 * Clears one overridden property — the per-property "revert to theme"
 * control. Prunes the section if that was its last override, and prunes the
 * whole design back to `undefined` if that was the last override anywhere,
 * so a fully-reverted widget round-trips as "no design object" rather than
 * a tree of empty `{}`s.
 */
export function revertChartDesignProperty(
  design: ChartDesign | undefined,
  path: ChartDesignPropertyPath,
): ChartDesign | undefined {
  if (!design) return design
  const { section, key } = PATH_MAP[path]
  const sectionValue = design[section] as Section | undefined
  if (!sectionValue || sectionValue[key] === undefined) return design

  const restSection: Section = { ...sectionValue }
  delete restSection[key]

  const next = { ...design } as Record<string, unknown>
  if (Object.keys(restSection).length > 0) {
    next[section] = restSection
  } else {
    delete next[section]
  }

  return Object.keys(next).length > 0 ? (next as ChartDesign) : undefined
}

/** Clears every override — the whole-chart "revert to theme" control. */
export function revertAllChartDesign(): undefined {
  return undefined
}

/**
 * One revert for the whole title group (#1174, Matt's decision) — the
 * horizontal title-format row carries a single revert control, not four
 * per-property buttons, so this drops the `title` section wholesale. The
 * stored model is unchanged: each title property still lives on its own and
 * `isChartDesignPropertyOverridden` still answers per property.
 */
export function revertChartDesignTitleGroup(
  design: ChartDesign | undefined,
): ChartDesign | undefined {
  if (!design || design.title === undefined) return design
  const next = { ...design } as Record<string, unknown>
  delete next.title
  return Object.keys(next).length > 0 ? (next as ChartDesign) : undefined
}

// ---------------------------------------------------------------------------
// Bar colour rules (#1808) — value-based bar colours. Same list helpers as the
// table widget's conditional formatting (tableDesign.ts), stored in
// design.bar_colors so chart templates carry them. `bar_colors` is pruned
// once it has no rules and no default, and the design once it is empty.
// ---------------------------------------------------------------------------

function withBarColors(design: ChartDesign | undefined, next: ChartBarColorsDesign): ChartDesign | undefined {
  const empty = next.rules.length === 0 && !next.default_color
  if (!empty) return { ...design, bar_colors: next }
  const rest = { ...design } as Record<string, unknown>
  delete rest.bar_colors
  return Object.keys(rest).length > 0 ? (rest as ChartDesign) : undefined
}

function currentBarColors(design: ChartDesign | undefined): ChartBarColorsDesign {
  return { ...design?.bar_colors, rules: design?.bar_colors?.rules ?? [] }
}

/** Appends a rule; it is evaluated after the existing ones (first match wins). */
export function addBarColorRule(design: ChartDesign | undefined, rule: TableConditionalFormatRule): ChartDesign {
  const current = currentBarColors(design)
  return withBarColors(design, { ...current, rules: [...current.rules, rule] }) as ChartDesign
}

/** Replaces the rule at `index`. */
export function updateBarColorRule(
  design: ChartDesign | undefined,
  index: number,
  rule: TableConditionalFormatRule,
): ChartDesign {
  const current = currentBarColors(design)
  return withBarColors(design, { ...current, rules: current.rules.map((r, i) => (i === index ? rule : r)) }) as ChartDesign
}

/** Removes the rule at `index`, pruning bar_colors (and the design) when nothing is left. */
export function removeBarColorRule(design: ChartDesign | undefined, index: number): ChartDesign | undefined {
  const current = currentBarColors(design)
  return withBarColors(design, { ...current, rules: current.rules.filter((_, i) => i !== index) })
}

/** Sets or clears (`undefined`) the colour for bars no rule matches. */
export function setBarColorDefault(design: ChartDesign | undefined, color: string | undefined): ChartDesign | undefined {
  const current = currentBarColors(design)
  const next: ChartBarColorsDesign = { rules: current.rules }
  if (color) next.default_color = color
  return withBarColors(design, next)
}
